import {
  getInboxThreadList,
  getInboxThreadDetail,
  markInboxThreadRead,
  setInboxThreadState,
  bulkSetInboxThreadState,
  replyToInboxThread,
  suggestInboxThreadReply,
  setInboxThreadTargetStatus,
  getInboxMetadata,
  getInboxUnreadCount,
  getInboxChannels,
  getInboxGlance,
  getChannelMessageThreadStatusTypes,
  getInboxConfig as fetchInboxConfigApi,
} from "@/api/inbox";
import { getTemplateListAPI, updateTargetStatus } from "@/api/sequence";
import { INBOX_CONFIG, INBOX_REFRESH_MIN_VISIBLE_MS, SUGGEST_MODE, MESSAGE_DIRECTION, CHANNEL_SLUG } from "@/views/inboxes/config";
import { stripHtmlToText, htmlToPlainText } from "@/utils";

const DEFAULT_PER_PAGE = INBOX_CONFIG.default_per_page;
const DEFAULT_ORDER_BY = INBOX_CONFIG.default_order_by;

// Returns null until the user module loads — actions early-out to avoid 401s.
function getProfileId(rootState) {
  const id = rootState?.user?.currentId?.profile_id;
  const parsed = parseInt(id);
  return Number.isFinite(parsed) ? parsed : null;
}

// Shared 4-key payload for /inbox/thread/* mutations.
function threadEnvelope(profile_id, thread, extras) {
  return Object.assign({
    profile_id,
    thread_external_id: thread.thread_external_id,
    channel_type_slug: thread.channel_type_slug,
    channel_account_external_id: thread.channel_account_external_id,
  }, extras || {});
}

// Swallow request.js errors after marking them already-toasted (no double toast).
async function silently(fn) {
  try { return await fn(); }
  catch (e) { if (e) e.popuped = true; return null; }
}

const state = {
  threadList: [],
  threadListLoading: false,
  threadListAppending: false,
  threadPagination: { page: 1, per_page: DEFAULT_PER_PAGE, total_entries: 0, total_pages: 0 },

  activeThread: null,
  activeThreadMessages: [],
  activeThreadLoading: false,
  activeThreadError: null,

  metadata: {
    all_count: 0, unread_count: 0, replied_count: 0, snoozed_count: 0,
    meeting_booked_count: 0, not_interested_count: 0, closed_count: 0, archived_count: 0,
  },

  navUnreadCount: 0,

  connectedChannels: [],   // from /inbox/channels — drives dynamic channel chips.
  glance: null,            // /inbox/glance: empty-pane KPIs + weekly sparkline.
  sequenceOptionsList: [], // lazy-loaded id+name list for the Sequence filter.
  threadStatusTypes: [],   // {id, slug, code}[] from Channel_Message_Thread_Status_Types.
  inboxConfig: null,       // { channel_limits, nav_unread_poll_interval_ms, ... } from /inbox/config.

  sort: { order_by: DEFAULT_ORDER_BY, is_ascending_order: false },

  // Channel_Templates for the composer popover (replaces inbox-only snippets).
  templates: [],

  suggestionsByThreadId: {},
  suggestionsLoadingByThreadId: {},

  filters: {
    channel_types: [],
    thread_status_slugs: [],
    sequence_ids: [],
    channel_account_ids: [],
    classifications: [],
    is_read: null,
    is_replied: null,
    include_archived: false,
    only_archived: false,
    only_done: false,
    start_date: "",
    end_date: "",
    search_term: "",
  },

  listAbortController: null,
};

const mutations = {
  setThreadList(state, payload) {
    state.threadList = Array.isArray(payload) ? payload : [];
  },
  appendThreadList(state, payload) {
    const incoming = Array.isArray(payload) ? payload : [];
    if (incoming.length === 0) return;
    // Pagination races can land the same page twice; dedupe before merging.
    const seen = new Set(state.threadList.map(t => t.thread_id));
    state.threadList = state.threadList.concat(incoming.filter(t => !seen.has(t.thread_id)));
  },
  setThreadListLoading(state, payload)   { state.threadListLoading   = !!payload; },
  setThreadListAppending(state, payload) { state.threadListAppending = !!payload; },
  setThreadPagination(state, payload) {
    state.threadPagination = Object.assign({}, state.threadPagination, payload || {});
  },
  setActiveThread(state, payload)         { state.activeThread = payload || null; },
  setActiveThreadMessages(state, payload) { state.activeThreadMessages = Array.isArray(payload) ? payload : []; },
  // Optimistic insert at index 0 (newest slot); dedupes by provider_message_id.
  appendActiveThreadMessage(state, msg) {
    if(!msg) return;
    const id = msg.provider_message_id;
    const existing = state.activeThreadMessages || [];
    if(id && existing.some(m => m.provider_message_id === id)) return;
    state.activeThreadMessages = [msg].concat(existing);
  },
  setActiveThreadLoading(state, payload)  { state.activeThreadLoading = !!payload; },
  setActiveThreadError(state, payload)    { state.activeThreadError = payload || null; },
  setMetadata(state, payload)             { state.metadata = Object.assign({}, state.metadata, payload || {}); },
  setNavUnreadCount(state, payload)       { state.navUnreadCount = Number(payload) || 0; },
  setGlance(state, payload)               { state.glance = payload || null; },
  setConnectedChannels(state, payload)    { state.connectedChannels = Array.isArray(payload) ? payload : []; },
  setSequenceOptions(state, payload)      { state.sequenceOptionsList = Array.isArray(payload) ? payload : []; },
  setThreadStatusTypes(state, payload)    { state.threadStatusTypes = Array.isArray(payload) ? payload : []; },
  setInboxConfig(state, payload)          { state.inboxConfig = payload || null; },
  setFilters(state, payload)              { state.filters = Object.assign({}, state.filters, payload || {}); },
  setSort(state, payload)                 { state.sort = Object.assign({}, state.sort, payload || {}); },
  setTemplates(state, payload) {
    state.templates = Array.isArray(payload) ? payload : [];
  },
  setListAbortController(state, payload) { state.listAbortController = payload; },
  setSuggestions(state, { thread_id, suggestions }) {
    state.suggestionsByThreadId = Object.assign({}, state.suggestionsByThreadId, { [thread_id]: suggestions || [] });
  },
  setSuggestionsLoading(state, { thread_id, loading }) {
    state.suggestionsLoadingByThreadId = Object.assign({}, state.suggestionsLoadingByThreadId, { [thread_id]: !!loading });
  },
  // Optimistic: flip is_read locally before the server confirms.
  applyOptimisticIsRead(state, { thread_id, is_read }) {
    state.threadList = state.threadList.map(t =>
      (t && t.thread_id == thread_id) ? Object.assign({}, t, { is_read: is_read ? 1 : 0 }) : t
    );
    if (state.activeThread && state.activeThread.thread_id == thread_id) {
      state.activeThread = Object.assign({}, state.activeThread, { is_read: is_read ? 1 : 0 });
    }
  },
};

const actions = {
  // Cancels prior inflight; aborted fetches skip finally to keep loading on until latest settles.
  async fetchThreadList({ state, commit, rootState }, { page, per_page, append } = {}) {
    const profile_id = getProfileId(rootState);
    if (!profile_id) return;

    if (state.listAbortController) state.listAbortController.abort();
    const controller = new AbortController();
    commit("setListAbortController", controller);
    commit(append ? "setThreadListAppending" : "setThreadListLoading", true);

    // Floor loading at 300ms for refresh of an already-populated list — a sub-100ms
    // cached response would flash too briefly to register as "applied".
    const is_refresh = !append && Array.isArray(state.threadList) && state.threadList.length > 0;
    const started_at = Date.now();

    try {
      const payload = Object.assign({}, state.filters, {
        profile_id,
        page: page || state.threadPagination.page,
        per_page: per_page || rootState.user.currentId.max_search_per_page || state.threadPagination.per_page,
        order_by: state.sort.order_by,
        is_ascending_order: !!state.sort.is_ascending_order,
      });
      const res = await getInboxThreadList(payload, controller.signal);
      const data = res?.data ?? { pagination: {}, threads: [] };
      commit(append ? "appendThreadList" : "setThreadList", data.threads || []);
      commit("setThreadPagination", data.pagination || {});
    } catch (error) {
      // A superseded request is aborted on purpose. Swallow only that rejection
      // (the old `return` inside `finally` did this implicitly) so the ~18
      // callers don't get an unhandled ERR_CANCELED on every rapid re-query.
      if (!controller.signal.aborted) throw error;
    } finally {
      if (!controller.signal.aborted) {
        if (is_refresh) {
          const remaining = INBOX_REFRESH_MIN_VISIBLE_MS - (Date.now() - started_at);
          if (remaining > 0) await new Promise(r => setTimeout(r, remaining));
        }
        commit("setThreadListLoading", false);
        commit("setThreadListAppending", false);
        commit("setListAbortController", null);
      }
    }
  },

  // Errors surface inline via activeThreadError; popuped:true suppresses the global toast.
  async fetchThreadDetail({ state, commit, rootState }, { thread }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id || !thread) return;
    commit("setActiveThreadLoading", true);
    commit("setActiveThreadError", null);
    try {
      const res = await getInboxThreadDetail(threadEnvelope(profile_id, thread));
      const data = res?.data ?? { thread: null, messages: [] };
      commit("setActiveThread", data.thread || null);
      // Preserve optimistic outbound messages whose provider_message_id isn't
      // yet in the canonical fetched list — Nylas webhook can lag 1-5s after
      // send, and without this the just-sent message vanishes between the
      // optimistic insert and the webhook arrival.
      const fetched = data.messages || [];
      const fetched_ids = new Set(fetched.map(m => m.provider_message_id).filter(Boolean));
      const optimistic_kept = (state.activeThreadMessages || []).filter(m =>
        m && m.is_optimistic && m.provider_message_id && !fetched_ids.has(m.provider_message_id)
      );
      // messages are newest-first (server ORDER BY date DESC; appendActiveThreadMessage
      // prepends at index 0). The just-sent optimistic message is the newest, so it goes
      // at the FRONT — appending it would render it at the top instead of above the composer.
      commit("setActiveThreadMessages", optimistic_kept.concat(fetched));
    } catch (e) {
      const status = e?.response?.status;
      const code = e?.response?.data?.code;
      let msg = e?.response?.data?.message;
      if (status == 400 && (code == 40006 || (msg && /not exist/i.test(msg)))) {
        msg = "thread_not_loadable";
      }
      commit("setActiveThread", null);
      commit("setActiveThreadMessages", []);
      commit("setActiveThreadError", msg || "thread_load_failed");
      if (e) e.popuped = true;
    } finally {
      commit("setActiveThreadLoading", false);
    }
  },

  // Optimistic mark-as-read.
  async markRead({ commit, rootState }, { thread, is_read }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id || !thread) return;
    const next_state = is_read === false ? false : true;
    commit("applyOptimisticIsRead", { thread_id: thread.thread_id, is_read: next_state });
    try {
      await markInboxThreadRead({
        profile_id,
        thread_external_id: thread.thread_external_id,
        channel_type_slug: thread.channel_type_slug,
        is_read,
      });
    } catch (e) {
      commit("applyOptimisticIsRead", { thread_id: thread.thread_id, is_read: !next_state });
      throw e;
    }
  },

  async setThreadState({ dispatch, rootState }, { thread, thread_status_slug, is_archived, snoozed_until_at }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id || !thread) return;
    await setInboxThreadState({
      profile_id,
      thread_external_id: thread.thread_external_id,
      channel_type_slug: thread.channel_type_slug,
      thread_status_slug,
      is_archived,
      snoozed_until_at,
    });
    await dispatch("fetchThreadDetail", { thread });
  },

  // Single round-trip for N threads; backend does one SQL statement.
  async bulkSetThreadState({ dispatch, rootState }, { threads, is_read, is_archived, thread_status_slug, snoozed_until_at }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id || !Array.isArray(threads) || threads.length === 0) return null;
    const payload = {
      profile_id,
      threads: threads.map(t => ({
        channel_type_slug: t.channel_type_slug,
        thread_external_id: t.thread_external_id,
      })),
    };
    if (is_read           != null) payload.is_read           = is_read;
    if (is_archived       != null) payload.is_archived       = is_archived;
    if (thread_status_slug != null) payload.thread_status_slug = thread_status_slug;
    if (snoozed_until_at  != null) payload.snoozed_until_at  = snoozed_until_at;
    const res = await bulkSetInboxThreadState(payload);
    await dispatch("fetchMetadata");
    await dispatch("fetchNavUnreadCount");
    return res?.data ?? null;
  },

  // Send then immediately drop an optimistic message in the thread (Nylas
   // webhook ingest lags 1-5s; without this the new message is invisible
   // until the next refetch hits the webhook row). Then refetch to pick up
   // the canonical row — appendActiveThreadMessage dedupes by message id.
  async sendReply({ commit, dispatch, rootState }, { thread, body, subject, to_emails, cc_emails, bcc_emails, tracking_options, attachments }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id || !thread) return null;
    // LinkedIn chat renders the body as literal text — send plain text, not composer HTML.
    const send_body = thread.channel_type_slug === CHANNEL_SLUG.LINKEDIN ? htmlToPlainText(body) : body;
    const res = await replyToInboxThread(threadEnvelope(profile_id, thread, {
      body: send_body, subject, to_emails, cc_emails, bcc_emails, tracking_options,
      attachments: (attachments?.length) ? attachments : null,
    }));
    const sent = res?.data;
    if(sent && sent.provider_message_id) {
      const from_email = thread.connected_account_email || "";
      const from_name  = thread.connected_account_name  || from_email;
      // Server-rendered body (Liquid attributes substituted, multi-line
      // normalized to HTML) — fall back to raw body if the API didn't
      // return it (older deploy).
      const display_body    = sent.rendered_body    || send_body || "";
      const display_subject = sent.rendered_subject || subject || thread.subject || "";
      commit("appendActiveThreadMessage", {
        provider_message_id: sent.provider_message_id,
        direction: MESSAGE_DIRECTION.OUTBOUND,
        subject: display_subject,
        body: display_body,
        snippet: stripHtmlToText(display_body).slice(0, INBOX_CONFIG.msg_preview_max),
        from: from_email ? [{ name: from_name, email: from_email }] : [],
        to:   (to_emails || []).map(e => ({ email: e })),
        cc:   (cc_emails || []).map(e => ({ email: e })),
        attachments: Array.isArray(sent.attachments) && sent.attachments.length
          ? sent.attachments
          : (attachments || []).map(a => ({ filename: a.filename, content_type: a.content_type, size_bytes: a.size || 0 })),
        message_at: sent.sent_at || new Date().toISOString(),
        open_count: 0, click_count: 0, last_open_at: null, last_click_at: null,
        click_breakdown: [],
        open_timestamps: [], click_timestamps: [],
        is_optimistic: true,
      });
    }
    await dispatch("fetchThreadDetail", { thread });
    return res?.data ?? null;
  },

  async fetchSuggestions({ commit, rootState }, { thread, mode, user_intent }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id || !thread) return [];
    const thread_id = thread.thread_id;
    const safe_mode = (mode === SUGGEST_MODE.REPLY || mode === SUGGEST_MODE.FOLLOW_UP) ? mode : SUGGEST_MODE.AUTO;
    const safe_intent = typeof user_intent === "string" ? user_intent.trim().slice(0, INBOX_CONFIG.user_intent_max_chars) : "";
    commit("setSuggestionsLoading", { thread_id, loading: true });
    try {
      const res = await suggestInboxThreadReply(threadEnvelope(profile_id, thread, {
        mode: safe_mode,
        user_intent: safe_intent || undefined,
      }));
      const suggestions = res?.data?.suggestions ?? [];
      commit("setSuggestions", { thread_id, suggestions });
      return suggestions;
    } catch (e) {
      if (e) e.popuped = true;
      return [];
    } finally {
      commit("setSuggestionsLoading", { thread_id, loading: false });
    }
  },

  // is_pause: true = pause this prospect's cadence, false = resume it. Reuses the SAME canonical
  // endpoint the sequence-detail prospect table uses (targets-actions handlePause / handlePlay):
  // updateTargetStatus sets is_paused true/false on the Sequence_Target — no inbox-specific path.
  async stopSequence({ dispatch, rootState }, { thread, is_pause = true }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id || !thread || !thread.sequence_target_id || !thread.sequence_identifier) return;
    await updateTargetStatus({
      profile_id,
      sequence_identifier: thread.sequence_identifier,
      sequence_targets: [thread.sequence_target_id],
      is_paused: !!is_pause,
      // Resuming also clears is_finished so the worker re-processes it (mirrors handlePlay).
      ...(is_pause ? {} : { is_finished: false }),
    });
    await dispatch("fetchThreadDetail", { thread });
  },

  // Quick actions → Sequence_Target state. Server whitelists action: interested | meeting_booked | not_interested.
  async setTargetStatus({ dispatch, rootState }, { thread, action }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id || !thread || !action) return null;
    const res = await setInboxThreadTargetStatus(threadEnvelope(profile_id, thread, { action }));
    await dispatch("fetchThreadDetail", { thread });
    return res?.data ?? null;
  },

  async fetchMetadata({ state, commit, rootState }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id) return;
    const res = await getInboxMetadata({
      profile_id,
      channel_types: state.filters.channel_types,
      sequence_ids: state.filters.sequence_ids,
      channel_account_ids: state.filters.channel_account_ids,
    });
    if (res?.data) commit("setMetadata", res.data);
  },

  async fetchGlance({ commit, rootState }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id) return;
    await silently(async () => {
      const res = await getInboxGlance({ profile_id });
      commit("setGlance", res?.data ?? null);
    });
  },

  async fetchConnectedChannels({ commit, rootState }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id) return;
    await silently(async () => {
      const res = await getInboxChannels({ profile_id });
      commit("setConnectedChannels", res?.data?.channels ?? []);
    });
  },

  // Loads Channel_Message_Thread_Status_Types once per session. Backend caches it.
  async fetchThreadStatusTypes({ state, commit }) {
    if (state.threadStatusTypes.length) return;
    await silently(async () => {
      const res = await getChannelMessageThreadStatusTypes();
      commit("setThreadStatusTypes", res?.data ?? []);
    });
  },

  // Loads /inbox/config (channel limits, poll interval, ...) once per session.
  async fetchInboxConfig({ state, commit }) {
    if (state.inboxConfig) return;
    await silently(async () => {
      const res = await fetchInboxConfigApi();
      commit("setInboxConfig", res?.data ?? null);
    });
  },

  // Delegates to sequence module so the dropdown honours subscription max_search_per_page.
  async fetchSequenceOptions({ commit, dispatch, rootState }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id) return;
    await silently(async () => {
      const rows = await dispatch("sequence/getSequenceListForFilter", { profile_id }, { root: true });
      // /sequences returns the primary key as sequence_id (s.id as sequence_id), never as id.
      commit("setSequenceOptions", (rows || []).map(s => ({ id: s.sequence_id, name: s.name, is_active: !!s.is_active })));
    });
  },

  async fetchNavUnreadCount({ commit, rootState }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id) return;
    const res = await getInboxUnreadCount({ profile_id });
    commit("setNavUnreadCount", res?.data?.count ?? 0);
  },

  // Quick-insert source for the composer popover — reuses canonical /channels/templates.
  async fetchInboxTemplates({ commit, rootState }) {
    const profile_id = getProfileId(rootState);
    if (!profile_id) return;
    await silently(async () => {
      const res = await getTemplateListAPI({
        profile_id,
        per_page: rootState.user.currentId.max_search_per_page,
        order_by: "last_modified",
        is_ascending_order: false,
      });
      const rows = res?.data?.channel_templates || [];
      commit("setTemplates", rows.map(r => ({
        template_id:         r.channel_template_id,
        template_identifier: r.channel_template_identifier,
        name:                r.name,
        channel_type_slug:   r.channel_type_slug,
        channel_node_slug:   r.channel_node_slug,
        body:    (r.channel_template_versions && r.channel_template_versions[0] && r.channel_template_versions[0].parameters && (r.channel_template_versions[0].parameters.content || r.channel_template_versions[0].parameters.message)) || "",
        subject: (r.channel_template_versions && r.channel_template_versions[0] && r.channel_template_versions[0].parameters && r.channel_template_versions[0].parameters.subject) || "",
        last_modified: r.last_modified,
      })));
    });
  },
};

const getters = {
  activeThreadId: (state) => state.activeThread ? state.activeThread.thread_id : null,
  suggestionsForActive: (state) => {
    const id = state.activeThread ? state.activeThread.thread_id : null;
    return id ? (state.suggestionsByThreadId[id] || []) : [];
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
